import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DatepickerService} from './datepicker.service';
import Months from './monts.data';

@Component({
    selector: 'app-datepicker',
    templateUrl: './datepicker.component.html',
    providers: [DatepickerService]
})
export class DatepickerComponent implements OnInit {

    /**
     * Формат даты, где
     * dd - день
     * mm - месяц
     * yyyy - год.
     * Разделитель и порядок могут быть любыми,
     * например dd.mm.yyyy или yyyy/mm/dd.
     *
     * Допускается указание формата с точностью лишь до месяца (например mm.yyyy)
     * или только года yyyy.
     * @type {string}
     */
    @Input() format = 'dd.mm.yyyy';
    @Input() startAt: string;               // Дата начала
    @Input() endWith: string;               // Дата окончания
    @Input() value: string;                 // Текущее значение даты, заданное в формате this.format

    /**
     * Событие выбора даты в пикере.
     * @type {EventEmitter<any>}
     */
    @Output() selected = new EventEmitter();

    /**
     * Тип календаря:
     * dd - выбор с точностью до дня
     * mm - выбор с точностью до месяца
     * yyyy - выбор года
     * @type {string}
     */
    type: 'dd' | 'mm' | 'yyyy' = 'dd';

    /**
     * Сегодняшняя дата
     * @type {Date}
     */
    today = new Date();

    /**
     * Текущая дата, которую отображаем (месяц и год. Конкретная дата не важна, берем по умолчанию первое число).
     * @type {Date}
     */
    date = new Date(this.today.getFullYear(), this.today.getMonth(), 1);

    /**
     * Выбранная пользователем дата
     */
    selectedDate: Date;

    /**
     * Видны ли селекты выбора месяца и года
     * @type {boolean}
     */
    isMonthSelectVisible = false;
    isYearSelectVisible = false;

    /**
     * Текущий отображаемый год в селекте выбора года.
     * От него будут отображаться доступные годы до и после для выбора.
     * @type {number}
     */
    currentVisibleYear = this.date.getFullYear();

    /**
     * Коллекции для отображения
     * Дни текущего месяца
     * */
    currentDaysList: any[] = [];

    /**
     * Дни предыдущего месяца
     * @type {Array}
     */
    prevDaysList: any[] = [];

    /**
     * Дни следующего месяца
     * @type {Array}
     */
    nextDaysList: any[] = [];

    /**
     * Список текущих месяцев
     * @type {Array}
     */
    currentMonthsList: any[] = [];

    /**
     * Список отображаемых лет
     * @type {Array}
     */
    currentYearsList: any[] = [];

    /**
     * Текст кнопки 'Сегодня'
     * @type {string}
     */
    todayButtonText = '';

    selectTransform = {
        m: '',
        y: '' // Сдвиг, чтобы текущий год был сразу под курсором мыши
    };

    constructor(
        private dateService: DatepickerService
    ) {
    }

    ngOnInit() {
        /**
         * На тот случай, если год в формате yyyy из поля пришел в числовом формате
         */
        if (typeof this.value === 'number') {
            this.value = '' + this.value;
        }
        /**
         * Определяем тип пикера в зависимости от указанного формата даты
         */
        if (this.format.indexOf('dd') >= 0) {
            this.type = 'dd';
        } else if (this.format.indexOf('mm') >= 0) {
            this.type = 'mm';
        } else {
            this.type = 'yyyy';
            this.startAt = '1900';
        }

        if (this.startAt) {
            this.dateService.setDateFrom(this.startAt, this.format);
        }

        if (this.endWith) {
            this.dateService.setDateTo(this.endWith, this.format);
        }

        /**
         * В зависимости от типа пикера (день, месяц, год) юудем отображать разное количество лет
         */
        const yearsToShow = this.type === 'mm' ? 7 : (this.type === 'yyyy' ? 15 : 12);
        this.dateService.setYearsToShow(yearsToShow);

        this.updateDateFromValue(this.value);

        this.updateDate();

        this.setTodayButtonText();
    }

    private updateDate() {
        this.updateMonths();
        this.updateYears();
        this.updateDays();
    }

    /**
     * Обновить списки отображаемых дней
     */
    private updateDays(): void {
        this.currentDaysList = this.dateService.getCurrentDays(this.date);
        this.prevDaysList = this.dateService.getDaysBeforeCurrent(this.date);
        this.nextDaysList = this.dateService.getDaysAfterCurrent(this.currentDaysList.length + this.prevDaysList.length);
    }

    /**
     * Обновить список отображаемых месяцев
     */
    private updateMonths(): void {
        this.currentMonthsList = this.dateService.getCurrentMonths(this.date);
    }

    /**
     * Обновить список отображаемых лет
     */
    private updateYears(): void {
        // Сколько лет отображать до текущей записи
        const beforeYearsCount = this.type === 'dd' ? 3 : (this.type === 'yyyy' ? 4 : null);
        this.currentYearsList = this.dateService.getCurrentYears(this.currentVisibleYear, beforeYearsCount);
    }

    showMonthSelect() {
        this.isMonthSelectVisible = true;
        const index = this.month;
        const itemHeight = 22 + 5; // height + margin-bottom
        const value = itemHeight * (index) + 5 + 2;
        this.selectTransform.m = `translateY(-${value}px)`;
    }

    hideMonthSelect() {
        this.isMonthSelectVisible = false;
    }

    showYearSelect() {
        this.currentVisibleYear = this.year;
        this.updateYears();
        this.isYearSelectVisible = true;

        // Ориентируемся на 4-ый год в списке
        const index = 3;
        const itemHeight = 22 + 5; // height + margin-bottom
        // высота элементов + высота кнопки 16px + 2px компенсации
        const value = itemHeight * (index) + 16 + 2;
        this.selectTransform.y = `translateY(-${value}px)`;
    }

    hideYearSelect() {
        this.isYearSelectVisible = false;
    }

    /**
     * При скролле селекты выбора города смещаем доступные годы на 5 лет вперед или назад, в зависимости от
     * направления скролла.
     * @param event
     */
    onYearScroll(event) {
        event.preventDefault();

        if (+this.currentVisibleYear <= 1904 && event.deltaY < 0) {
            return;
        }

        if (+this.currentVisibleYear >= 2090 && event.deltaY >= 0) {
            return;
        }

        /**
         * Корректируем, насколько крутить вверх или вниз отображаемые годы,
         * чтобы не превышать границы 1900 и 2100 более, чем это необходимо для заполнения
         * @type {number}
         */
        const bottomDiff = this.currentVisibleYear - 4 - 1900;
        const topDiff = 2100 - 10 - this.currentVisibleYear;

        let count = 6;

        if (bottomDiff <= 3 || topDiff <= 3) {
            count = 3;
        }

        this.currentVisibleYear += event.deltaY >= 0 ? count : (count * -1);
        this.updateYears();
    }

    /**
     * При скролле месяцев будем менять текущий год
     * @param event
     */
    onMonthScroll(event) {
        event.preventDefault();
        const newYear = this.year + +(event.deltaY >= 0 ? 1 : -1);
        this.currentVisibleYear = newYear;
        this.setYear(newYear);
    }

    /**
     * Изменяем текущий отображаемый год в селекте на значение value
     * @param {number} value
     */
    incrementVisibleYearByValue(value: number) {
        if (this.format === 'yyyy') {

            /**
             * @TODO Костылик, чтобы не писать посторную обработку. Нужно пофиксить код
             */
            this.onYearScroll({
                deltaY: value,
                preventDefault: function () {}
            });
            return;
        }

        this.currentVisibleYear += value;
        this.updateYears();
    }

    /**
     * Установить месяц для отображения в календаре
     * @param month
     * @param event
     */
    setMonth(month, event?: Event) {
        if (event && event.preventDefault) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (month < 0) {
            this.date.setMonth(11);
            this.setYear(this.year - 1);
        } else if (month > 11) {
            this.date.setMonth(0);
            this.setYear(this.year + 1);
        } else {
            this.date.setMonth(month);
            this.updateDate();
        }

        /**
         * Чтобы в момент клика селект еще был отображен и поле с датой не считало выбор месяца кликом вне поля
         */
        setTimeout(() => {
            this.hideMonthSelect();
        });
    }

    /**
     * Установить год для отображения в календаре
     * @param year
     * @param event
     */
    setYear(year, event?: Event) {

        if (event && event.preventDefault) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.date.setFullYear(year);
        this.updateDate();
    }

    /**
     * Выбрать дату в календаре
     * @param day
     * @param event
     */
    selectDate(day, event?: Event) {
        if (event && event.preventDefault) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.selectedDate = new Date(this.year, this.month, day);
        this.dateService.setSelectedDate(this.selectedDate);

        this.updateDate();

        this.selected.emit({
            date: this.selectedDate,
            string: this.dateToFormat(this.selectedDate)
        });
    }

    selectMonth(monthIndex: number) {
        this.setMonth(monthIndex);
        this.selectDate(1);
    }

    selectYear(year: number) {
        this.setYear(year);
        this.setMonth(0);
        this.selectDate(1);
    }

    /**
     * Изменить месяц на следующий/предыдущий при скролле на календаре
     * @param event
     */
    onScroll(event) {
        event.preventDefault();

        if (event.deltaY >= 0) {
            this.setMonth(this.month + 1);
        } else {
            this.setMonth(this.month - 1);
        }
    }

    updateDateFromValue(value: string) {
        const date = this.stringToDate(value);

        if (!date) {
            return;
        }

        this.selectedDate = new Date(date);
        this.date = new Date(date);
        this.date.setDate(1); // Нормализация даты, с которой работаем, чтобы корректно высчитывать новые даты на основании этой.
        this.currentVisibleYear = date.getFullYear();
        this.dateService.setSelectedDate(this.selectedDate);
    }

    get month() {
        return this.date.getMonth();
    }

    get year() {
        return this.date.getFullYear();
    }

    /**
     * Выбрать текущий день
     */
    setToday(event?: Event) {
        if (event && event.preventDefault) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.date = new Date(this.today);
        this.date.setDate(1);
        this.selectDate(this.today.getDate());
        this.currentVisibleYear = this.today.getFullYear();
    }

    /**
     * Преобразует дату в строку заданного формата (this.format)
     * @param {Date} date
     * @returns {string}
     */
    dateToFormat(date: Date): string {
        return this.dateService.dateToFormat(date, this.format);
    }

    /**
     * Преобразует строку в формате this.format в объект даты.
     * Делаем это вручную, а не с помощью Date.parse, чтобы добиться кроссбраузерности.
     * @param {string} string
     * @returns {Date}
     */
    stringToDate(string: string): Date {
        return this.dateService.stringToDate(string, this.format);
    }

    isToday() {
        if (!this.selectedDate) {
            return false;
        }

        const selDateString = this.dateService.dateToFormat(this.selectedDate, this.format);
        const todayDateString = this.dateService.dateToFormat(this.today, this.format);

        return selDateString === todayDateString;
    }

    /**
     * Установить текстовое представление сегодняшней даты для кнопки 'Сегодня'
     */
    setTodayButtonText() {
        let text = '';

        /**
         * Для дня и года это строка в формате this.format, для месяца это строка "месяц yyyy", например "январь 2017"
         */
        if (this.type === 'mm') {
            text = Months[this.today.getMonth()].toLowerCase() + ' ' + this.today.getFullYear();
        } else {
            text = this.dateService.dateToFormat(this.today, this.format);
        }

        this.todayButtonText = text;
    }
}
